import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogOut, Trash2, RefreshCw, CarFront } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_META = {
  nouveau: { label: "Nouveau", cls: "bg-[#0066FF]/15 text-[#00E5FF] border-[#0066FF]/40" },
  confirme: { label: "Confirmé", cls: "bg-amber-500/15 text-amber-400 border-amber-500/40" },
  traite: { label: "Traité", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" },
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#07090E] noise-overlay relative">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md rounded-md border border-white/10 bg-[#0E1322]/80 backdrop-blur-xl p-8 md:p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="rounded-md bg-white px-2.5 py-1.5 flex items-center">
            <img src="/logo-emblem.png" alt="Logo AMP Services 01" className="h-9 w-auto" />
          </span>
          <div>
            <div className="font-display font-extrabold uppercase tracking-tight">Espace Pro</div>
            <div className="font-mono-tech text-[10px] tracking-[0.3em] text-[#00E5FF] uppercase mt-0.5">AMP Services 01</div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label htmlFor="admin-email" className="block text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-400 mb-2">Email</label>
            <input
              id="admin-email"
              data-testid="admin-input-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-white/10 bg-[#07090E] px-4 py-3 text-sm outline-none focus:border-[#0066FF] transition-colors duration-300"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-400 mb-2">Mot de passe</label>
            <input
              id="admin-password"
              data-testid="admin-input-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-white/10 bg-[#07090E] px-4 py-3 text-sm outline-none focus:border-[#0066FF] transition-colors duration-300"
            />
          </div>
          {error && (
            <div data-testid="admin-login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-4 py-2.5">
              {error}
            </div>
          )}
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-[#0066FF] hover:bg-[#0052CC] disabled:opacity-60 text-white font-semibold py-3 flex items-center justify-center gap-2 transition-colors duration-300"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Se connecter
          </button>
        </form>

        <Link to="/" data-testid="admin-back-home-link" className="block mt-6 text-center text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 hover:text-white transition-colors duration-300">
          Retour au site
        </Link>
      </motion.div>
    </div>
  );
};

const VehiclePanel = ({ vehicles, vform, setVform, onAdd, onRemove }) => {
  const set = (key) => (e) => setVform((f) => ({ ...f, [key]: e.target.value }));
  const inputCls =
    "w-full rounded-md border border-white/10 bg-[#07090E] px-4 py-2.5 text-sm outline-none focus:border-[#0066FF] transition-colors duration-300";
  return (
    <div data-testid="admin-vehicles-panel">
      <form onSubmit={onAdd} className="rounded-md border border-white/10 bg-[#0E1322] p-6 mb-8 grid md:grid-cols-6 gap-4">
        <input data-testid="vehicle-input-titre" required placeholder="Titre (ex : Peugeot 208 1.2 PureTech)" value={vform.titre} onChange={set("titre")} className={`${inputCls} md:col-span-3`} />
        <input data-testid="vehicle-input-prix" required type="number" min="0" placeholder="Prix (€)" value={vform.prix} onChange={set("prix")} className={inputCls} />
        <input data-testid="vehicle-input-annee" required type="number" min="1950" max="2100" placeholder="Année" value={vform.annee} onChange={set("annee")} className={inputCls} />
        <input data-testid="vehicle-input-km" required type="number" min="0" placeholder="Kilométrage" value={vform.km} onChange={set("km")} className={inputCls} />
        <select data-testid="vehicle-input-energie" value={vform.energie} onChange={set("energie")} className={inputCls}>
          {["Essence", "Diesel", "Hybride", "Électrique", "GPL"].map((e) => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
        <input data-testid="vehicle-input-image" type="url" placeholder="URL photo (optionnel)" value={vform.image} onChange={set("image")} className={`${inputCls} md:col-span-3`} />
        <input data-testid="vehicle-input-lien" type="url" placeholder="Lien annonce leboncoin (optionnel)" value={vform.lien} onChange={set("lien")} className={`${inputCls} md:col-span-2`} />
        <button data-testid="vehicle-add-button" type="submit" className="rounded-md bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-semibold py-2.5 transition-colors duration-300">
          Ajouter
        </button>
      </form>

      {vehicles.length === 0 ? (
        <div data-testid="admin-vehicles-empty" className="rounded-md border border-white/10 bg-[#0E1322] py-16 text-center text-slate-400 text-sm">
          Aucun véhicule publié. Ajoutez votre première annonce ci-dessus.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {vehicles.map((v) => (
            <div key={v.id} data-testid={`admin-vehicle-${v.id}`} className="rounded-md border border-white/10 bg-[#0E1322] p-5 flex items-center gap-4">
              <div className="w-20 h-16 rounded-sm bg-[#07090E] overflow-hidden shrink-0 flex items-center justify-center">
                {v.image ? (
                  <img src={v.image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <CarFront className="w-7 h-7 text-[#0066FF]/40" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold truncate">{v.titre}</div>
                <div className="text-xs text-slate-400 font-mono-tech mt-1">
                  {Number(v.prix).toLocaleString("fr-FR")} € · {v.annee} · {Number(v.km).toLocaleString("fr-FR")} km · {v.energie}
                </div>
              </div>
              <button
                data-testid={`admin-vehicle-delete-${v.id}`}
                onClick={() => onRemove(v.id)}
                className="rounded-md border border-white/10 hover:border-red-500 hover:text-red-400 p-2.5 transition-colors duration-300 shrink-0"
                aria-label="Supprimer"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("demandes");
  const [vehicles, setVehicles] = useState([]);
  const [vform, setVform] = useState({ titre: "", prix: "", annee: "", km: "", energie: "Essence", image: "", lien: "" });

  const loadVehicles = useCallback(async () => {
    try {
      const { data } = await api.get("/vehicles");
      setVehicles(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  const addVehicle = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...vform, prix: Number(vform.prix), annee: Number(vform.annee), km: Number(vform.km) };
      if (!payload.image) delete payload.image;
      if (!payload.lien) delete payload.lien;
      const { data } = await api.post("/vehicles", payload);
      setVehicles((vs) => [data, ...vs]);
      setVform({ titre: "", prix: "", annee: "", km: "", energie: "Essence", image: "", lien: "" });
      toast.success("Véhicule publié sur le site");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeVehicle = async (id) => {
    try {
      await api.delete(`/vehicles/${id}`);
      setVehicles((vs) => vs.filter((v) => v.id !== id));
      toast.success("Véhicule retiré");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/contact");
      setRequests(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadVehicles();
  }, [load, loadVehicles]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/contact/${id}`, { status });
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Statut mis à jour");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/contact/${id}`);
      setRequests((rs) => rs.filter((r) => r.id !== id));
      toast.success("Demande supprimée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = filter === "all" ? requests : requests.filter((r) => r.status === filter);

  return (
    <div className="min-h-screen bg-[#07090E] px-6 lg:px-10 py-10">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <div className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#00E5FF]">Espace Pro</div>
            <h1 className="font-display font-extrabold uppercase tracking-tight text-2xl md:text-3xl mt-2">
              Demandes clients
            </h1>
            <div data-testid="admin-user-email" className="text-sm text-slate-400 mt-1">{user.email}</div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="rounded-full border border-white/15 hover:border-[#0066FF] p-3 transition-colors duration-300"
              aria-label="Rafraîchir"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={onLogout}
              className="rounded-full border border-white/15 hover:border-red-500 hover:text-red-400 px-5 py-2.5 text-sm font-semibold flex items-center gap-2 transition-colors duration-300"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-8 border-b border-white/10 pb-4" data-testid="admin-tabs">
          {[
            ["demandes", "Demandes clients"],
            ["vehicules", "Véhicules à vendre"],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                tab === key ? "bg-[#0066FF] text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "vehicules" ? (
          <VehiclePanel vehicles={vehicles} vform={vform} setVform={setVform} onAdd={addVehicle} onRemove={removeVehicle} />
        ) : (
        <>
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            ["all", "Toutes"],
            ["nouveau", "Nouvelles"],
            ["confirme", "Confirmées"],
            ["traite", "Traitées"],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-filter-${key}`}
              onClick={() => setFilter(key)}
              className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors duration-300 border ${
                filter === key
                  ? "bg-[#0066FF] border-[#0066FF] text-white"
                  : "border-white/15 text-slate-400 hover:border-white/40"
              }`}
            >
              {label}
              <span className="ml-2 opacity-70">
                {key === "all" ? requests.length : requests.filter((r) => r.status === key).length}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#0066FF]" />
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="admin-empty-state" className="rounded-md border border-white/10 bg-[#0E1322] py-20 text-center text-slate-400 text-sm">
            Aucune demande pour le moment.
          </div>
        ) : (
          <div data-testid="admin-requests-table" className="space-y-4">
            {filtered.map((r) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-md border border-white/10 bg-[#0E1322] p-6"
                data-testid={`admin-request-${r.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-display font-bold text-lg">{r.nom}</span>
                      <span className={`rounded-full border px-3 py-0.5 text-[10px] font-mono-tech uppercase tracking-[0.2em] ${STATUS_META[r.status]?.cls || STATUS_META.nouveau.cls}`}>
                        {STATUS_META[r.status]?.label || r.status}
                      </span>
                      {r.user_id && (
                        <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-0.5 text-[10px] font-mono-tech uppercase tracking-[0.2em] text-emerald-400">
                          Compte client
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-400 mt-2 space-x-4">
                      <a href={`tel:${r.telephone}`} className="text-[#00E5FF] hover:underline">{r.telephone}</a>
                      {r.email && <span>{r.email}</span>}
                      {r.immatriculation && <span className="font-mono-tech">{r.immatriculation}</span>}
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-500 font-mono-tech">
                    {new Date(r.created_at).toLocaleString("fr-FR")}
                  </div>
                </div>
                <div className="mt-4 text-sm text-slate-300">
                  <span className="text-slate-500">Service :</span> {r.service}
                  {r.date_souhaitee && <span className="ml-4"><span className="text-slate-500">Date :</span> {r.date_souhaitee}</span>}
                </div>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{r.message}</p>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <select
                    data-testid={`admin-status-select-${r.id}`}
                    value={r.status}
                    onChange={(e) => updateStatus(r.id, e.target.value)}
                    className="rounded-md border border-white/10 bg-[#07090E] px-3 py-2 text-xs outline-none focus:border-[#0066FF]"
                  >
                    <option value="nouveau">Nouveau</option>
                    <option value="confirme">Confirmé</option>
                    <option value="traite">Traité</option>
                  </select>
                  <button
                    data-testid={`admin-delete-${r.id}`}
                    onClick={() => remove(r.id)}
                    className="rounded-md border border-white/10 hover:border-red-500 hover:text-red-400 px-3 py-2 text-xs flex items-center gap-2 transition-colors duration-300"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Supprimer
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        try {
          const { data } = await api.post("/auth/refresh");
          setUser(data);
        } catch {
          setUser(null);
        }
      }
    };
    check();
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
    }
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-[#07090E] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#0066FF]" />
      </div>
    );
  }

  if (user && user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#07090E] flex flex-col items-center justify-center gap-5 px-6 text-center">
        <p data-testid="admin-forbidden" className="text-slate-300">Cet espace est réservé à l'administrateur.</p>
        <Link to="/compte" className="rounded-full bg-[#0066FF] hover:bg-[#0052CC] text-white font-semibold px-6 py-3 transition-colors duration-300">
          Aller à mon espace client
        </Link>
      </div>
    );
  }

  return user ? <Dashboard user={user} onLogout={logout} /> : <Login onLogin={setUser} />;
}
